import { enqueueSnackbar as defaultEnqueueSnackbar } from 'notistack';

// Default timeout for API error notifications (matching old noty timeout)
export const API_NOTIFICATION_TIMEOUT = 4000;

/**
 * Show error notification using notistack
 * @param {string} message - Error message to display
 * @param {Function} enqueueSnackbar - Optional enqueueSnackbar function from hook context
 * @param {Object} options - Additional options
 */
export const showError = (message, enqueueSnackbar = defaultEnqueueSnackbar, options = {}) => {
  const defaultOptions = {
    variant: 'error',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
    autoHideDuration: API_NOTIFICATION_TIMEOUT,
    ...options,
  };

  if (enqueueSnackbar && typeof enqueueSnackbar === 'function') {
    enqueueSnackbar(message || 'Something went wrong...', defaultOptions);
  } else {
    console.error('showError: enqueueSnackbar function not available', message);
  }
};

/**
 * Show success notification using notistack
 * @param {string} message - Success message to display
 * @param {Function} enqueueSnackbar - Optional enqueueSnackbar function from hook context
 * @param {Object} options - Additional options
 */
export const showSuccess = (message, enqueueSnackbar = defaultEnqueueSnackbar, options = {}) => {
  const defaultOptions = {
    variant: 'success',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
    autoHideDuration: API_NOTIFICATION_TIMEOUT,
    ...options,
  };

  if (enqueueSnackbar && typeof enqueueSnackbar === 'function') {
    enqueueSnackbar(message, defaultOptions);
  } else {
    console.error('showSuccess: enqueueSnackbar function not available', message);
  }
};

/**
 * Show info notification using notistack
 * @param {string} message - Info message to display
 * @param {Function} enqueueSnackbar - Optional enqueueSnackbar function from hook context
 * @param {Object} options - Additional options
 */
export const showInfo = (message, enqueueSnackbar = defaultEnqueueSnackbar, options = {}) => {
  const defaultOptions = {
    variant: 'info',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
    autoHideDuration: API_NOTIFICATION_TIMEOUT,
    ...options,
  };

  if (enqueueSnackbar && typeof enqueueSnackbar === 'function') {
    enqueueSnackbar(message, defaultOptions);
  } else {
    console.error('showInfo: enqueueSnackbar function not available', message);
  }
};

/**
 * Show warning notification using notistack
 * @param {string} message - Warning message to display
 * @param {Function} enqueueSnackbar - Optional enqueueSnackbar function from hook context
 * @param {Object} options - Additional options
 */
export const showWarning = (message, enqueueSnackbar = defaultEnqueueSnackbar, options = {}) => {
  const defaultOptions = {
    variant: 'warning',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
    autoHideDuration: API_NOTIFICATION_TIMEOUT,
    ...options,
  };

  if (enqueueSnackbar && typeof enqueueSnackbar === 'function') {
    enqueueSnackbar(message, defaultOptions);
  } else {
    console.error('showWarning: enqueueSnackbar function not available', message);
  }
};

type TNotification = {
  /** Anything else falls back to an info notification. */
  type?: 'error' | 'success' | 'info' | 'warning';
  text: string;
  /** Auto hide duration; defaults to `API_NOTIFICATION_TIMEOUT`. */
  timeout?: number;
};

/**
 * Generic notification function with type parameter (legacy noty style)
 * @param {Function} enqueueSnackbar - Optional enqueueSnackbar function from hook context
 */
export const showNotification = (
  { type, text, timeout }: TNotification,
  enqueueSnackbar = defaultEnqueueSnackbar,
) => {
  const options = {
    autoHideDuration: timeout || API_NOTIFICATION_TIMEOUT,
  };

  switch (type) {
    case 'error':
      showError(text, enqueueSnackbar, options);
      break;
    case 'success':
      showSuccess(text, enqueueSnackbar, options);
      break;
    case 'info':
      showInfo(text, enqueueSnackbar, options);
      break;
    case 'warning':
      showWarning(text, enqueueSnackbar, options);
      break;
    default:
      showInfo(text, enqueueSnackbar, options);
      break;
  }
};
